import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector , useDispatch} from 'react-redux';
import styled from 'styled-components';
import { STATES } from '../../util/constant';
import { updateProfile, updateEmailProfile } from '../../sagas/actions';
import { actions } from '../../util/actions';
import API from '../../util/api';
import { showErrorModal } from "../../util/general";
import { useLocation } from 'react-router-dom';

const EyeIcon = (props) => (
  <svg
    viewBox="0 0 576 512"
    width="1em"
    height="1em"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path d="M572.52 241.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400a144 144 0 1 1 144-144 143.93 143.93 0 0 1-144 144zm0-240a95.31 95.31 0 0 0-25.31 3.79 47.85 47.85 0 0 1-66.9 66.9A95.78 95.78 0 1 0 288 160z" />
  </svg>
);

const EyeSlashIcon = (props) => (
  <svg
    viewBox="0 0 640 512"
    width="1em"
    height="1em"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path d="M320 400c-75.85 0-137.25-58.71-142.9-133.11L72.2 185.82c-13.79 17.3-26.48 35.59-36.72 55.59a32.35 32.35 0 0 0 0 29.19C89.71 376.41 197.07 448 320 448c26.91 0 52.87-4 77.89-10.46L346 397.39a144.13 144.13 0 0 1-26 2.61zm313.82 58.1l-110.55-85.44a331.25 331.25 0 0 0 81.25-102.07 32.35 32.35 0 0 0 0-29.19C550.29 135.59 442.93 64 320 64a308.15 308.15 0 0 0-147.32 37.7L45.46 3.37A16 16 0 0 0 23 6.18L3.37 31.45A16 16 0 0 0 6.18 53.9l588.36 454.73a16 16 0 0 0 22.46-2.81l19.64-25.27a16 16 0 0 0-2.82-22.45zm-183.72-142l-39.3-30.38A94.75 94.75 0 0 0 416 256a94.76 94.76 0 0 0-121.31-92.21A47.65 47.65 0 0 1 304 192a46.64 46.64 0 0 1-1.54 10l-73.61-56.89A142.31 142.31 0 0 1 320 112a143.92 143.92 0 0 1 144 144c0 21.63-5.29 41.79-13.9 60.11z" />
  </svg>
);

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const MAX_PASSWORD_LENGTH = 128;

const ProfilePageContainer = styled.div`
  width: 1000px;
  margin: 0 auto;
  padding: 20px;
  min-height: 75vh;

  @media (max-width: ${breakpoints.tablet}px) {
    width: 100%;
    padding: 16px;
  }
  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    padding: 12px;
  }
`;

const Title = styled.h1`
  color: #fff;
`;

const SectionTitle = styled.h4`
  color: ${({ colors }) => colors.titleText};
  background-color: ${({ colors }) => colors.componentHeaderBackground};
  padding: 10px;
  border-radius: 5px 5px 0 0;
  margin: 0;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 1rem;
    padding: 8px;
    text-align: center;
  }
`;

const Form = styled.div`
  background-color: ${({ colors }) => colors.formBackground};
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 0 0 5px 5px;

  @media (max-width: ${breakpoints.phone}px) {
    padding: 16px;
  }
`;

const InlineContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const LabelInline = styled.label`
  font-weight: bold;
  color: ${({ colors }) => colors.labelText};
  width: 100px;
  text-align: left;

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

const NarrowLabel = styled(LabelInline)`
  width: auto;
  margin-bottom: 0;
  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

const Input = styled.input`
  width: 60%;
  padding: 8px;
  border-radius: 5px;
  border: 1px solid #2C4960;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

const NarrowInput = styled(Input)`
  flex: 1;
  min-width: 80px;
  width: auto;
  margin-bottom: 0;
  margin-top: 4px;
  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    margin-top: 0;
  }
`;

const Select = styled.select`
  width: 60%;
  padding: 8px;
  margin-bottom: 15px;
  border: 1px solid ${({ colors }) => colors.inputBorder};
  border-radius: 5px;
  background-color: ${({ colors }) => colors.formBackground};
  color: ${({ colors }) => colors.labelText};
  font-size: 12px;
  appearance: none;

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    margin-bottom: 12px;
  }
`;

const NarrowSelect = styled(Select)`
  flex: 1;
  min-width: 100px;
  width: auto;
  margin-bottom: 0;
  margin-top: 4px;
  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    margin-top: 0;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: ${({ colors }) => colors.labelText};
  font-weight: bold;
  margin: 20px 0;

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const Checkbox = styled.input`
  margin-right: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  margin-top: 10px;
  gap: 8px;

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: row;
    justify-content: space-between;
    gap: 8px;
  }
`;

const Button = styled.button`
  padding: 8px 16px;         
  background-color: ${({ colors }) => colors.buttonBackground};
  color: ${({ colors }) => colors.buttonText};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-right: 10px;
  font-weight: bold;

  &:hover {
    background-color: ${({ colors }) => colors.buttonHover};
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 48%;              
    text-align: center;
     margin-right: 3px;
    padding: 8px 0;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${({ colors }) => colors.buttonBackground};
  color: ${({ colors }) => colors.buttonText};
`;

const EyeIconButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #195174;
  background: none;
  border: none;
  padding: 0;
  z-index: 9999;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  padding: 10px;
  margin-bottom: 10px;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px;
  margin-bottom: 10px;
`;

const InstructionText = styled.p`
  font-size: 0.9rem;
  color: ${({ colors }) => colors?.labelText || "#195174"};
  margin: 10px 0 0;
`;

const PasswordLinkWrapper = styled.div`
  width: 60%;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const PasswordLink = styled.button`
  background: none;
  border: none;
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 0.95rem;
  font-weight: bold;

  &:hover {
    color: ${({ colors }) =>
      colors.buttonHoverBackground || colors.buttonHover || colors.buttonBackground};
  }
`;

const PasswordFieldWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
`;

const PasswordFieldInput = styled(Input)`
  width: 100%;
`;

const CriteriaList = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CriteriaItem = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ $met }) => ($met ? "green" : "#c53030")};
`;

const SubscriptionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1em;
`;

const SubscriptionRow = styled.tr`
  border-top: 1px solid #ccc;
`;

const SubscriptionCell = styled.td`
  padding: 0.75em;
  vertical-align: top;
  font-size: 13px;

  &:first-child { width: 5%; }
  &:nth-child(2) { width: 55%; }
  &:nth-child(3) { width: 40%; }

  @media (max-width: ${breakpoints.phone}px) {
    display: block;
    width: 100%;
    &:not(:first-child) {
      margin-top: 8px;
    }
  }
`;

const SubscriptionsMessage = styled.div`
  padding: 0.75em 0;
  font-size: 0.9rem;
  color: ${({ colors }) => colors.labelText};
`;

const ProfilePage = ({
  colors,
  userOverride = null,
  teacherSubscriptionsOverride = null,
  teacherSubscriptionsLoadingOverride = null,
  teacherSubscriptionsErrorOverride = null,
  onCancel: onCancelProp,
}) => {
  console.log(colors);
  const storeUser = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const lastLocationKeyRef = useRef(location.key);
  const storeSubscriptions = useSelector((s) => s.auth?.teacher_subscriptions);
  console.log(storeSubscriptions)
  const rawUser = userOverride ?? storeUser ?? {};
  const resolvedUser = rawUser?.user ?? rawUser;
  const user = Array.isArray(resolvedUser)
    ? resolvedUser[0] || {}
    : resolvedUser || {};
  const usingSubscriptionsOverride = teacherSubscriptionsOverride !== null;
  const teacherSubscriptionsSource = usingSubscriptionsOverride
    ? teacherSubscriptionsOverride
    : storeSubscriptions;
  const teacherSubscriptions = Array.isArray(teacherSubscriptionsSource)
    ? teacherSubscriptionsSource
    : teacherSubscriptionsSource
    ? Object.values(teacherSubscriptionsSource)
    : [];
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState('');
  const effectiveSubscriptionsLoading = usingSubscriptionsOverride
    ? Boolean(teacherSubscriptionsLoadingOverride)
    : subscriptionsLoading;
  const effectiveSubscriptionsError = usingSubscriptionsOverride
    ? teacherSubscriptionsErrorOverride || ''
    : subscriptionsError;
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);
  // Individual state variables for each field
  const [title, setTitle] = useState(user.title || "");
  const [firstName, setFirstName] = useState(user.first_name || "");
  const [lastName, setLastName] = useState(user.last_name || "");
  const [address, setAddress] = useState(user.address || "");
  const [address2, setAddress2] = useState(user.address2 || "");
  const [city, setCity] = useState(user.city || "");
  const [state, setState] = useState(STATES[user.state] || user.state || "");
  const [zip, setZip] = useState(user.zip || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [ext, setExt] = useState(user.ext || "");
  const [fax, setFax] = useState(user.fax || "");
  const [email, setEmail] = useState(user.email || "");
  const [newsletter, setNewsletter] = useState(user.newsletter_signup || "");

  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordHasInteracted, setPasswordHasInteracted] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [passLength, setPassLength] = useState(false);
  const [hasLower, setHasLower] = useState(false);
  const [hasUpper, setHasUpper] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecial, setHasSpecial] = useState(false);
  const [isPasswordTooLong, setIsPasswordTooLong] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  
  // State to track if changes were made
  useEffect(() => {
    setTitle(user.title || "");
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setAddress(user.address || user.street || "");
    setAddress2(user.address2 || user.address_2 || "");
    setCity(user.city || "");
    setState(STATES[user.state] || user.state || "");
    setZip(user.zip || user.postal_code || "");
    setPhone(user.phone || "");
    setExt(user.ext || "");
    setFax(user.fax || "");
    setEmail(user.email || "");
    setNewsletter(user.newsletter_signup || user.news_letter || "");
  }, [user]);

  useEffect(() => {
    if (usingSubscriptionsOverride) return;
    if (!user?.user_id) return;
    if (teacherSubscriptions.length > 0 || subscriptionsLoading || subscriptionsError) return;

    setSubscriptionsError('');
    setSubscriptionsLoading(true);

    API.getTeacherSubscriptions({ user_id: user.user_id })
      .then((response) => {
        if (isUnmountedRef.current) return;
        dispatch({
          type: actions.STORE_TEACHER_SUBSCRIPTIONS,
          getTeacherSubscriptions: response,
        });
      })
      .catch((err) => {
        if (isUnmountedRef.current) return;
        setSubscriptionsError(
          err?.message || 'Unable to load teacher subscriptions.'
        );
      })
      .finally(() => {
        if (isUnmountedRef.current) return;
        setSubscriptionsLoading(false);
      });
  }, [
    dispatch,
    subscriptionsError,
    subscriptionsLoading,
    teacherSubscriptions.length,
    teacherSubscriptionsOverride,
    usingSubscriptionsOverride,
    user?.user_id,
  ]);

  const isPersonalInfoChanged =
    title !== (user.title || "") ||
    firstName !== (user.first_name || "") ||
    lastName !== (user.last_name || "") ||
    address !== (user.address || user.street || "") ||
    city !== (user.city || "") ||
    state !== (STATES[user.state] || user.state || "") ||
    zip !== (user.zip || user.postal_code || "");

  const emailChanged = email !== (user.email || "");

  const contactFieldsChanged =
    phone !== (user.phone || "") ||
    ext !== (user.ext || "") ||
    fax !== (user.fax || "") ||
    newsletter !== (user.newsletter_signup || user.news_letter || "");

  const isContactInfoChanged = emailChanged || contactFieldsChanged;

  const handleUpdateContactInfo = async () => {
    const contactSnapshot = {
      phone,
      ext,
      fax,
      newsletter,
    };

    try {
      if (emailChanged) {
        const emailResponse = await API.updateUserEmail(email, user.user_id);
        if (emailResponse.status !== 'Success') {
          showErrorModal('Email Update Failed: ' + (emailResponse.message || 'Unknown error'));
          return;
        }
        if (!userOverride) {
          dispatch(updateEmailProfile(email));
        }
      }

      if (contactFieldsChanged) {
        await handleUpdatePersonalInfo(contactSnapshot);
      } else if (emailChanged) {
        alert('Email updated successfully.');
      }
    } catch (error) {
      showErrorModal('Contact Information Update Failed: ' + (error?.message || 'Unknown error'));
    }
  };

  const handleStartChangePassword = () => {
    setChangingPassword(true);
    setPasswordMessage('');
    setPasswordError('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordHasInteracted(false);
    setConfirmTouched(false);
    setPassLength(false);
    setHasLower(false);
    setHasUpper(false);
    setHasNumber(false);
    setHasSpecial(false);
    setIsPasswordTooLong(false);
  };

  const handleCancelPasswordChange = useCallback(() => {
    setChangingPassword(false);
    setPasswordSubmitting(false);
    setPasswordMessage('');
    setPasswordError('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordHasInteracted(false);
    setConfirmTouched(false);
    setPassLength(false);
    setHasLower(false);
    setHasUpper(false);
    setHasNumber(false);
    setHasSpecial(false);
    setIsPasswordTooLong(false);
  }, []);

  useEffect(() => {
    if (changingPassword && location.key !== lastLocationKeyRef.current) {
      handleCancelPasswordChange();
    }
    lastLocationKeyRef.current = location.key;
  }, [location.key, changingPassword, handleCancelPasswordChange]);

  const handleSubmitPasswordChange = async () => {
    setPasswordMessage('');
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please complete all password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    const requirementsMet =
      passLength && hasLower && hasUpper && hasNumber && hasSpecial && !isPasswordTooLong;

    if (!requirementsMet) {
      setPasswordError(
        'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.'
      );
      return;
    }

    setPasswordSubmitting(true);
    try {
      const response = await API.passwordReset({
        email: user.email,
        current_password: currentPassword,
        password: newPassword,
        c_password: confirmPassword,
        user_id: user.user_id
      });

      if (response.status === 'Success') {
        setPasswordMessage(response.message || 'Password updated successfully.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordHasInteracted(false);
        setConfirmTouched(false);
        setPassLength(false);
        setHasLower(false);
        setHasUpper(false);
        setHasNumber(false);
        setHasSpecial(false);
        setIsPasswordTooLong(false);
      } else {
        setPasswordError(response.message || 'Unable to update password. Please try again.');
      }
    } catch (error) {
      setPasswordError(error.message || 'Unable to update password. Please try again.');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleUpdatePersonalInfo = async (overrides = {}) => {
    const payload = {
      title: overrides.title ?? title,
      first_name: overrides.first_name ?? firstName,
      last_name: overrides.last_name ?? lastName,
      address: overrides.address ?? address,
      address2: overrides.address2 ?? address2,
      city: overrides.city ?? city,
      state: overrides.state ?? state,
      zip: overrides.zip ?? zip,
      phone: overrides.phone ?? phone,
      ext: overrides.ext ?? ext,
      fax: overrides.fax ?? fax,
      newsletter: overrides.newsletter ?? newsletter,
      newsletter_signup: (overrides.newsletter ?? newsletter) ? 1 : 0,
      user_id: overrides.user_id ?? user.user_id,
      email: overrides.email ?? email,
    };
    const response = await API.updateUserProfile(payload);
    if (response.status === "Success") {
        if (!userOverride) {
          dispatch(updateProfile(payload));
        }
        alert("User Profile Update Successfully");
    } else {
        showErrorModal("User Profile Update Failed: " + response.message);
    }
  };

  // const handleUpdateContactInfo = async () => {
  //   const payload = { phone, ext, fax, newsletter_signup: newsletter ? 1 : 0 };
  //   await API.updateUserProfile(payload);
  // };

  const handleCancel = () => {
    if (onCancelProp) {
      onCancelProp();
      return;
    }
    window.location.reload();
  };

  const pageTitle = userOverride
    ? `${(user.first_name || "").trim()} ${(user.last_name || "").trim()}`.trim() || "User Info"
    : "My Info";
  const displayTitle = changingPassword ? "Set New Password" : pageTitle;
  const passwordCriteria = [
    { label: 'At least 8 characters', met: passLength },
    { label: 'At least 1 lowercase letter', met: hasLower },
    { label: 'At least 1 uppercase letter', met: hasUpper },
    { label: 'At least 1 number', met: hasNumber },
    { label: 'At least 1 special character', met: hasSpecial },
  ];
  const passwordsMatch = newPassword === confirmPassword;
  const passwordRequirementsMet =
    passLength && hasLower && hasUpper && hasNumber && hasSpecial && !isPasswordTooLong;
  const passwordFormReady =
    currentPassword &&
    newPassword &&
    confirmPassword &&
    passwordRequirementsMet &&
    passwordsMatch;
  const matchMessageColor = passwordsMatch ? '#2f855a' : '#c53030';
  const matchMessage = passwordsMatch ? 'Passwords match.' : 'Passwords must match.';

  return (
    <ProfilePageContainer colors={colors}>
      <Title style={{color: '#fff'}}>{displayTitle}</Title>
      {!changingPassword && (
      <>
      <SectionTitle colors={colors}>Personal Information</SectionTitle>
      <Form colors={colors}>
        <InlineContainer>
          <LabelInline htmlFor="title" colors={colors}>Title</LabelInline>
          <Select id="title" value={title} onChange={(e) => setTitle(e.target.value)} colors={colors}>
            <option value=""></option>
            <option>Brother</option>
            <option>Dr.</option>
            <option>Father</option>
            <option>Miss</option>
            <option>Mr.</option>
            <option>Mrs.</option>
            <option>Ms.</option>
            <option>Pastor</option>
            <option>Reverend</option>
            <option>Sister</option>
          </Select>
        </InlineContainer>

        <InlineContainer>
          <LabelInline htmlFor="firstName" colors={colors}>First Name</LabelInline>
          <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} colors={colors} />
        </InlineContainer>

        <InlineContainer>
          <LabelInline htmlFor="lastName" colors={colors}>Last Name</LabelInline>
          <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} colors={colors} />
        </InlineContainer>

        <InlineContainer>
          <LabelInline htmlFor="address" colors={colors}>Address</LabelInline>
          <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} colors={colors} />
        </InlineContainer>

        <InlineContainer>
          <LabelInline htmlFor="city" colors={colors}>City</LabelInline>
          <NarrowInput id="city" value={city} onChange={(e) => setCity(e.target.value)} colors={colors} />
          <NarrowLabel htmlFor="state" colors={colors}>State</NarrowLabel>
          <NarrowSelect id="state" value={state} onChange={(e) => setState(e.target.value)} colors={colors}>
            <option value="">Select a state</option>
            {Object.entries(STATES).map(([stateName, stateCode]) => (
              <option key={stateCode} value={stateCode}>
                {stateName}
              </option>
            ))}
          </NarrowSelect>
          <NarrowLabel htmlFor="zip" colors={colors}>Zip</NarrowLabel>
          <NarrowInput id="zip" value={zip} onChange={(e) => setZip(e.target.value)} colors={colors} />
        </InlineContainer>

        <CancelButton colors={colors} onClick={handleCancel}>{'<<'}Cancel</CancelButton>
        <Button colors={colors} onClick={handleUpdatePersonalInfo} disabled={!isPersonalInfoChanged}>Update Info</Button>
      </Form>

      <SectionTitle colors={colors}>Contact Information</SectionTitle>
      <Form colors={colors}>
        <InlineContainer>
          <LabelInline htmlFor="email" colors={colors}>Email</LabelInline>
          <Input id="email" value={email} onChange={(e) => setEmail(e.target.value)} colors={colors} />
        </InlineContainer>
        <InlineContainer>
          <LabelInline colors={colors}>Password</LabelInline>
          <PasswordLinkWrapper>
            <PasswordLink
              type="button"
              colors={colors}
              onClick={handleStartChangePassword}
            >
              Change password
            </PasswordLink>
          </PasswordLinkWrapper>
        </InlineContainer>
        <InlineContainer>
          <LabelInline htmlFor="phone" colors={colors}>Phone</LabelInline>
          <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} colors={colors} />
          <NarrowLabel htmlFor="ext" colors={colors}>Ext</NarrowLabel>
          <NarrowInput id="ext" value={ext} onChange={(e) => setExt(e.target.value)} colors={colors} />
          <NarrowLabel htmlFor="fax" colors={colors}>Fax</NarrowLabel>
          <NarrowInput id="fax" value={fax} onChange={(e) => setFax(e.target.value)} colors={colors} />
        </InlineContainer>

        <CheckboxLabel colors={colors}>
          <Checkbox
            type="checkbox"
            checked={newsletter}
            onChange={(e) => setNewsletter(e.target.checked)}
          />
          Sign up for the ISAFE Newsletters and receive email updates.
        </CheckboxLabel>

        <CancelButton colors={colors} onClick={handleCancel}>{'<<'}Cancel</CancelButton>
        <Button colors={colors} onClick={handleUpdateContactInfo} disabled={!isContactInfoChanged}>Update Contact Info</Button>
      </Form>
      <SectionTitle colors={colors}>Subscription Memberships</SectionTitle>
      <Form colors={colors}>
          <SubscriptionsTable>
        <tbody>
          {effectiveSubscriptionsLoading && (
            <tr>
              <td colSpan={3}>
                <SubscriptionsMessage colors={colors}>
                  Loading teacher subscriptions…
                </SubscriptionsMessage>
              </td>
            </tr>
          )}
          {!effectiveSubscriptionsLoading && effectiveSubscriptionsError && (
            <tr>
              <td colSpan={3}>
                <SubscriptionsMessage colors={colors}>
                  {effectiveSubscriptionsError}
                </SubscriptionsMessage>
              </td>
            </tr>
          )}
          {!effectiveSubscriptionsLoading && !effectiveSubscriptionsError && teacherSubscriptions.length === 0 && (
            <tr>
              <td colSpan={3}>
                <SubscriptionsMessage colors={colors}>
                  No subscription memberships found.
                </SubscriptionsMessage>
              </td>
            </tr>
          )}
          {teacherSubscriptions.map((sub) => (
            <React.Fragment key={sub.invoice_id}>
              {/* Invoice header */}
              <tr>
                <td colSpan={3}>
                  <h3>
                    {sub.org_name} ({sub.invoice_number})
                  </h3>
                </td>
              </tr>

              {/* Details row */}
              <SubscriptionRow>
                <SubscriptionCell>&nbsp;</SubscriptionCell>

                <SubscriptionCell>
                  <b>
                    {sub.org_name} School ID&nbsp;
                    <span style={{ color: 'red' }}>
                      {sub.customer_id}
                    </span>
                  </b>
                  <br/>
                  <span style={{ fontSize: '10px' }}>
                    (Active: {sub.start_date} – {sub.end_date})
                  </span>
                  <br/>
                  {sub.org_city}, {sub.org_state} {sub.org_zip}
                </SubscriptionCell>

                <SubscriptionCell>
                  Access to curriculum, manage assigned implementations. (Subscription Teacher)
                </SubscriptionCell>
              </SubscriptionRow>
            </React.Fragment>
          ))}
        </tbody>
      </SubscriptionsTable> 
      </Form>
      </>
      )}

      {changingPassword && (
        <>
          <SectionTitle colors={colors}>Please enter your new password.</SectionTitle>
          <Form colors={colors}>
            {passwordMessage && <SuccessMessage>{passwordMessage}</SuccessMessage>}
            {passwordError && <ErrorMessage>{passwordError}</ErrorMessage>}
            <InlineContainer>
              <LabelInline htmlFor="currentPassword" colors={colors}>
                Current Password
              </LabelInline>
              <PasswordFieldWrapper>
                <PasswordFieldInput
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  colors={colors}
                />
                <EyeIconButton
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                >
                  {showCurrentPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </EyeIconButton>
              </PasswordFieldWrapper>
            </InlineContainer>
            <InlineContainer>
              <LabelInline htmlFor="newPassword" colors={colors}>
                New Password
              </LabelInline>
              <PasswordFieldWrapper>
                <PasswordFieldInput
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewPassword(value);
                    setPasswordHasInteracted(true);
                    setIsPasswordTooLong(value.length > MAX_PASSWORD_LENGTH);
                    setPassLength(value.length >= 8);
                    setHasLower(/[a-z]/.test(value));
                    setHasUpper(/[A-Z]/.test(value));
                    setHasNumber(/\d/.test(value));
                    setHasSpecial(/[!@#$%^&*(),.?":{}|<>]/.test(value));
                    if (confirmTouched) {
                      setConfirmTouched(false);
                    }
                  }}
                  onBlur={() => setPasswordHasInteracted(true)}
                  colors={colors}
                />
                <EyeIconButton
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                >
                  {showNewPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </EyeIconButton>
              </PasswordFieldWrapper>
            </InlineContainer>
            <InlineContainer>
              <LabelInline htmlFor="confirmPassword" colors={colors}>
                Confirm Password
              </LabelInline>
              <PasswordFieldWrapper>
                <PasswordFieldInput
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordHasInteracted(true);
                  }}
                  onBlur={() => setConfirmTouched(true)}
                  colors={colors}
                />
                <EyeIconButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </EyeIconButton>
              </PasswordFieldWrapper>
            </InlineContainer>
            {passwordHasInteracted && (
              <CriteriaList>
                {passwordCriteria.map((item) => (
                  <CriteriaItem key={item.label} $met={item.met}>
                    {item.label}
                  </CriteriaItem>
                ))}
              </CriteriaList>
            )}
            {confirmTouched && confirmPassword && (
              <InstructionText style={{ color: matchMessageColor }}>
                {matchMessage}
              </InstructionText>
            )}
            {isPasswordTooLong && (
              <InstructionText style={{ color: '#c53030' }}>
                Password cannot exceed {MAX_PASSWORD_LENGTH} characters.
              </InstructionText>
            )}
            <ButtonGroup>
              <CancelButton
                type="button"
                colors={colors}
                onClick={handleCancelPasswordChange}
              >
                {'<<'} Cancel
              </CancelButton>
              <Button
                type="button"
                colors={colors}
                onClick={handleSubmitPasswordChange}
                disabled={passwordSubmitting || !passwordFormReady}
              >
                {passwordSubmitting ? 'Saving...' : 'Set New Password'}
              </Button>
            </ButtonGroup>
          </Form>
        </>
      )}
    </ProfilePageContainer>
  );
};

export default ProfilePage;
